import { create } from 'zustand'

export interface TimelineEvent {
  id: string
  type: string
  timestamp: string
  sourceId: string
  sourceName: string
  targetId: string
  targetName: string
  properties: Record<string, unknown>
}

interface TimelineState {
  selectedEntityKey: string | null
  globalTimeRange: { start: string; end: string } | null
  temporalEvents: TimelineEvent[]
  entityKeyToNodeId: Record<string, string>
  nodeIdToEntityKey: Record<string, string>
  // null = no pattern filter active (show everything);
  // Set (possibly empty) = pattern filter active, only these graph node ids are visible.
  visibleGraphNodeIds: Set<string> | null

  // Time filtering
  filterStart: string | null
  filterEnd: string | null
  playheadTime: string | null
  isPlaying: boolean
  playSpeed: number

  // View window (zoom/pan)
  viewStart: string | null // ISO — left edge of visible ruler (null = full range)
  viewEnd: string | null // ISO — right edge of visible ruler

  selectEntity: (key: string | null) => void
  setGlobalTimeRange: (range: { start: string; end: string } | null) => void
  setTemporalEvents: (events: TimelineEvent[]) => void
  setEntityMapping: (keyToNode: Record<string, string>, nodeToKey: Record<string, string>) => void
  setVisibleGraphNodeIds: (ids: Set<string> | null) => void
  setFilterWindow: (start: string | null, end: string | null) => void
  setPlayheadTime: (time: string | null) => void
  setIsPlaying: (playing: boolean) => void
  setPlaySpeed: (speed: number) => void
  setViewWindow: (start: string | null, end: string | null) => void
  resetZoom: () => void
  reset: () => void
}

const INITIAL: Pick<
  TimelineState,
  | 'selectedEntityKey'
  | 'globalTimeRange'
  | 'temporalEvents'
  | 'entityKeyToNodeId'
  | 'nodeIdToEntityKey'
  | 'visibleGraphNodeIds'
  | 'filterStart'
  | 'filterEnd'
  | 'playheadTime'
  | 'isPlaying'
  | 'playSpeed'
  | 'viewStart'
  | 'viewEnd'
> = {
  selectedEntityKey: null,
  globalTimeRange: null,
  temporalEvents: [],
  entityKeyToNodeId: {},
  nodeIdToEntityKey: {},
  visibleGraphNodeIds: null,
  filterStart: null,
  filterEnd: null,
  playheadTime: null,
  isPlaying: false,
  playSpeed: 1,
  viewStart: null,
  viewEnd: null,
}

export const useTimelineStore = create<TimelineState>(set => ({
  ...INITIAL,
  selectEntity: key => set({ selectedEntityKey: key }),
  setGlobalTimeRange: range => set({ globalTimeRange: range }),
  setTemporalEvents: events => set({ temporalEvents: events }),
  setEntityMapping: (keyToNode, nodeToKey) =>
    set({ entityKeyToNodeId: keyToNode, nodeIdToEntityKey: nodeToKey }),
  setVisibleGraphNodeIds: ids => set({ visibleGraphNodeIds: ids }),
  setFilterWindow: (start, end) => set({ filterStart: start, filterEnd: end }),
  setPlayheadTime: time => set({ playheadTime: time }),
  setIsPlaying: playing => set({ isPlaying: playing }),
  setPlaySpeed: speed => set({ playSpeed: speed }),
  setViewWindow: (start, end) => set({ viewStart: start, viewEnd: end }),
  resetZoom: () => set({ viewStart: null, viewEnd: null }),
  reset: () => set(INITIAL),
}))
